import { useAuth as useClerkAuth, useUser } from '@clerk/react';
import { useEffect, useState } from 'react';

import { deriveRoleFlags, latchEmail, type EmailLatch } from './roleDecision';

/**
 * Synchronous role/identity hook. Reads everything from Clerk's
 * client-side state — no `/api/me` round-trip, no React-Query cache,
 * no stale-data failure mode.
 *
 * Why no backend call: `/api/me` was the chokepoint for two production
 * incidents (the 401 storm fixed in #235, and the inscrutable
 * /accounts↔/portal flicker for brian@inputrecovery.com). The role
 * comes from data Clerk has already given us:
 *
 *   - `email`  →  matches `@inputrecovery.com` → practitioner
 *   - `org_id` →  present → customer (active org membership)
 *
 * If both apply (a practitioner who joined a test customer org), the
 * org binding wins — matches the backend's `_provision_clerk_user`
 * order. If neither applies, the user has no derived role and the
 * frontend treats them as "still loading" / "unknown."
 *
 * **Security note**: this is for UX (chrome + redirects). The actual
 * authorization boundary is the backend (`require_practitioner` /
 * `_require_portal_user`). A user spoofing their email in DevTools
 * would change the rendered UI but couldn't access protected data.
 */

interface CurrentRole {
  /** Clerk userId, stable per user. Used as TanStack queryKey suffix. */
  userKey: string | null;
  email: string | null;
  isPractitioner: boolean;
  isCustomer: boolean;
  /** True iff Clerk has finished loading. Gate redirect logic on this. */
  isAuthReady: boolean;
  /** True iff Clerk has a session. */
  isSignedIn: boolean;
  /** Clerk org id when present — useful for the portal's "which account am I on" lookup. */
  orgId: string | null;
}

export function useCurrentRole(): CurrentRole {
  const { isLoaded, isSignedIn, userId, orgId } = useClerkAuth();
  const { user } = useUser();
  const liveEmail = user?.primaryEmailAddress?.emailAddress ?? null;

  // Latch the email so a transient `useUser()` reload (Clerk briefly nulls
  // `user` while refreshing the session on window refocus) can't flip the
  // derived role and bounce the user off an internal page (INP-502).
  // `isAuthReady` stays gated on the session's `isLoaded` (which does NOT
  // blip) — never on the user resource's load state — so the guard never
  // unmounts an authenticated page mid-session.
  const [latch, setLatch] = useState<EmailLatch>(() => ({
    userId: userId ?? null,
    email: liveEmail,
  }));
  useEffect(() => {
    setLatch((prev) => latchEmail(prev, { userId: userId ?? null, liveEmail }));
  }, [userId, liveEmail]);

  const email = liveEmail ?? (latch.userId === (userId ?? null) ? latch.email : null);
  const { isPractitioner, isCustomer } = deriveRoleFlags(email, orgId ?? null);

  return {
    userKey: userId ?? null,
    email,
    isPractitioner,
    isCustomer,
    isAuthReady: isLoaded,
    isSignedIn: isSignedIn ?? false,
    orgId: orgId ?? null,
  };
}
